/**
 * The connection picker, for a setting that may hold a `@role` instead of an id.
 *
 * Four inspectors ask the same question — which vault? — and any of them may be
 * looking at a node that names a role. Rendering that case once keeps the answer
 * consistent, and keeps the shape of the inspector column identical either way:
 * a Select where a Select was, not an alert that shoves everything down.
 *
 * A role is shown, never edited. Picking a connection here would silently
 * replace the role with an id and quietly undo the thing roles exist for —
 * promoting a validated pipeline without editing it.
 */

import { Select } from "@mantine/core";

import { ROLE_PREFIX } from "./useConnectionRef";

export function ConnectionRefSelect({
  label,
  description,
  placeholder,
  data,
  connectionId,
  role,
  settled,
  editable,
  onChange,
  nothingFoundMessage,
}: {
  label: string;
  description?: string;
  placeholder?: string;
  /** The connections that may be picked. Ignored while a role is in force. */
  data: { value: string; label: string }[];
  /** What the setting resolves to — empty while a role is still resolving. */
  connectionId: string;
  /** The role the setting names, if it names one. */
  role: string | null;
  /** False while a role is being looked up, so "loading" reads differently
   *  from "bound to nothing". */
  settled: boolean;
  editable: boolean;
  onChange: (value: string) => void;
  nothingFoundMessage?: string;
}) {
  if (role) {
    const unbound = settled && !connectionId;
    return (
      <Select
        label={label}
        description={
          unbound
            ? undefined
            : `Set by the role @${role} — which is what lets this pipeline be promoted without editing it.`
        }
        error={
          unbound
            ? `Nothing is bound to @${role} here. Set it on Administration → Connection roles; until then this step cannot list the vault's objects or fields.`
            : undefined
        }
        // The role is its own option so it shows whether or not it resolves.
        data={[{ value: ROLE_PREFIX + role, label: `@${role}` }]}
        value={ROLE_PREFIX + role}
        disabled
        readOnly
      />
    );
  }

  return (
    <Select
      label={label}
      description={description}
      placeholder={placeholder}
      data={data}
      value={connectionId || null}
      onChange={(v) => onChange(v ?? "")}
      disabled={!editable}
      searchable
      nothingFoundMessage={nothingFoundMessage}
    />
  );
}
